import { useMemo } from 'react';

import { queryOptions, useQuery } from '@tanstack/react-query';

import { OrganizationMember } from '@cast/types';
import { makeIsEnabled } from '@cast/utils';

import { apiClient } from '@cast/infrastructure-api';

import { overrideOrganizationHeader } from '@cast/api-client';
import { useOrganizations } from '@cast/shared-organization-context-data-access';

export const ORGANIZATION_USERS_QUERY_KEY = 'organization-users';
const getOrganizationMembersKey = (params: {
  organizationId: string | undefined;
  includeUserGroups: any;
}) =>
  [
    ORGANIZATION_USERS_QUERY_KEY,
    params.organizationId,
    params.includeUserGroups,
  ] as const;

const getOrganizationMembersQueryOptions = ({
  organizationId,
  includeUserGroups = false,
  enabled = true,
}: {
  organizationId?: string;
  includeUserGroups?: boolean;
  enabled?: boolean;
}) =>
  queryOptions({
    queryKey: getOrganizationMembersKey({ organizationId, includeUserGroups }),
    queryFn: async () => {
      const { data } = await apiClient.user.getOrganizationUsers(
        {
          organizationId: organizationId!,
          includeGroups: includeUserGroups,
        },
        {
          headers: overrideOrganizationHeader(organizationId),
        }
      );
      return data.users;
    },
    enabled: makeIsEnabled(!!organizationId, enabled),
  });

type UseOrganizationMembersQueryArgs = {
  enabled?: boolean;
  organizationId?: string;
  includeUserGroups?: boolean;
};

export const useOrganizationMembersQuery = (
  args?: UseOrganizationMembersQueryArgs
) => {
  const {
    enabled = true,
    includeUserGroups = false,
    organizationId,
  } = args || {};
  const { currentOrganization } = useOrganizations();
  const resolvedOrganizationId = organizationId ?? currentOrganization?.id;
  const query = useQuery(
    getOrganizationMembersQueryOptions({
      organizationId: resolvedOrganizationId,
      includeUserGroups,
      enabled,
    })
  );

  const members: OrganizationMember[] | undefined = useMemo(
    () =>
      query.data?.map(({ user, ...memberDetails }) => ({
        ...memberDetails,
        ...user,
      })) as OrganizationMember[] | undefined,
    [query.data]
  );

  return { members, ...query };
};
